'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Spinner } from '@galapagos/design-system/web';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useStepHeading } from '../hooks/useStepHeading';

/**
 * How long the screen stays before leaving for Personify: the gate's decision
 * log is sent once observability is ready, and this short pause lets it go
 * out instead of racing the navigation away.
 */
export const PERSONIFY_REDIRECT_DELAY_MS = 500;

/**
 * Personify Health's members sign in to the Personify portal before signup
 * starts, as on the legacy signup app. Rendered in place by the signup entry,
 * like the legacy loading overlay, so no route or screen id is added.
 */
export function PersonifyLoginNotice({ authorizeUrl }: { authorizeUrl: string }) {
  const { t } = useTranslation('signup');
  const title = t('personifyLogin.title');
  const headingRef = useStepHeading(title);
  const sent = useRef(false);
  ensureSignupStepStyle();

  useEffect(() => {
    if (sent.current) return;
    const timer = setTimeout(() => {
      sent.current = true;
      // `replace`, so Back from Personify's login never lands here again.
      window.location.replace(authorizeUrl);
    }, PERSONIFY_REDIRECT_DELAY_MS);
    // Strict Mode's mount-cleanup-mount clears this timer; the second mount
    // schedules a fresh one because nothing was sent yet.
    return () => clearTimeout(timer);
  }, [authorizeUrl]);

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <Spinner accessibilityLabel={title} />
        <p
          className="signup-page-title__description"
          role="status"
          aria-live="polite"
          data-testid="signup-personify-status"
        >
          {t('personifyLogin.status')}
        </p>
        <Link
          label={t('personifyLogin.link')}
          href={authorizeUrl}
          actionId="signup.personify-login-link.activate"
          data-testid="signup-personify-link"
        />
      </div>
    </SignupShell>
  );
}
